import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFilteredClimbsList } from '../../hooks/useClimbFilters';
import type { Climb } from '../../types/climb';
import { climbListPath, type ClimbListLocationType } from './climb-list-path';

interface ClimbNavigationProps {
  currentClimbSlug: string;
  gymSlug: string;
  locationId: string | number | undefined;
  /** Set when swiping within a board tab, so prev/next keep the board URL. */
  boardId?: string;
  locationType?: ClimbListLocationType;
}

export default function ClimbNavigation({
  currentClimbSlug,
  gymSlug,
  locationId,
  boardId,
  locationType = 'gym'
}: ClimbNavigationProps) {
  const navigate = useNavigate();
  const { getFilteredClimbs, saveFilteredClimbs } = useFilteredClimbsList(locationId);
  const [climbs, setClimbs] = useState<Climb[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [, setIsLoading] = useState(true);

  // Load filtered climbs from session storage
  useEffect(() => {
    const { climbs: storedClimbs, currentIndex: storedIndex } = getFilteredClimbs();

    if (storedClimbs.length > 0) {
      setClimbs(storedClimbs);

      // Find the current climb in the list
      const foundIndex = storedClimbs.findIndex(climb => climb.slug === currentClimbSlug);
      if (foundIndex !== -1) {
        setCurrentIndex(foundIndex);
        // Update the stored index
        saveFilteredClimbs(storedClimbs, foundIndex);
      } else {
        setCurrentIndex(storedIndex);
      }
    }

    setIsLoading(false);
  }, [currentClimbSlug, getFilteredClimbs, saveFilteredClimbs]);

  const navigateToClimb = useCallback((climb: Climb, newIndex: number) => {
    // Update current index in session storage
    saveFilteredClimbs(climbs, newIndex);

    // Keep last_viewed_climb in sync so ClimbsList highlights the right climb on back navigation
    sessionStorage.setItem(`last_viewed_climb_${locationId}`, climb.slug);

    navigate(climbListPath(climb, gymSlug, { boardId, locationType }), { replace: true, state: { climb } });
  }, [climbs, saveFilteredClimbs, navigate, gymSlug, boardId, locationType, locationId]);

  const goToPrevious = useCallback(() => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      navigateToClimb(climbs[newIndex], newIndex);
    }
  }, [currentIndex, climbs, navigateToClimb]);

  const goToNext = useCallback(() => {
    if (currentIndex < climbs.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      navigateToClimb(climbs[newIndex], newIndex);
    }
  }, [currentIndex, climbs, navigateToClimb]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goToPrevious();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        goToNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToPrevious, goToNext]);

  // Handle touch/swipe gestures
  useEffect(() => {
    let startX = 0;
    let startY = 0;
    let isTrackingSwipe = false;
    const minSwipeDistance = 80;

    const handleTouchStart = (event: TouchEvent) => {
      // Ignore touches that start inside a dialog, sheet, overlay, or a horizontal
      // scroller that owns its own left/right gestures (e.g. the video carousel).
      if ((event.target as Element).closest('[role="dialog"], [data-radix-popper-content-wrapper], [data-fullscreen-overlay], [data-swipe-ignore]')) {
        isTrackingSwipe = false;
        return;
      }
      isTrackingSwipe = true;
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
    };

    const handleTouchEnd = (event: TouchEvent) => {
      if (!isTrackingSwipe) return;
      const endX = event.changedTouches[0].clientX;
      const endY = event.changedTouches[0].clientY;
      const deltaX = Math.abs(endX - startX);
      const deltaY = Math.abs(endY - startY);

      // Only trigger if horizontal movement dominates (not a scroll)
      if (deltaX > minSwipeDistance && deltaX > deltaY * 2) {
        if (endX > startX) {
          goToPrevious();
        } else {
          goToNext();
        }
      }
    };

    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [goToPrevious, goToNext]);

  return null;
}
